import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  FiSend, 
  FiPaperclip, 
  FiX, 
  FiUsers, 
  FiMessageCircle,
  FiArrowLeft,
  FiSearch,
  FiMessageSquare,
  FiPlus,
  FiUser,
  FiRefreshCw,
  FiFile
} from 'react-icons/fi';
import api from '../../../config/api';
import { useWebSocket } from '../../../config/websocket';
import './Chat.css';

const Chat = () => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  const [showNewChat, setShowNewChat] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [typingUsers, setTypingUsers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);

  const activeConversationRef = useRef(activeConversation);
  const messagesRef = useRef(messages);
  const conversationsRef = useRef(conversations);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const typingDebounceRef = useRef(null);

  const currentUserId = localStorage.getItem('current_user_id');
  const jwtToken = localStorage.getItem('jwt_token');

  // FIXED: Use ref to prevent infinite loops
  const triggerBottomNavUpdate = useCallback(() => {
    const currentConversations = conversationsRef.current;
    if (!currentConversations || currentConversations.length === 0) return;

    console.log('🔄 Triggering BottomNav update from Chat component');
    
    try {
      // Calculate total unread count
      const totalUnread = currentConversations.reduce((total, conv) => {
        return total + (conv.unread_count || 0);
      }, 0);
      
      // Only update if changed to prevent loops
      const currentUnread = localStorage.getItem('global_unread_count');
      if (currentUnread !== totalUnread.toString()) {
        localStorage.setItem('global_unread_count', totalUnread.toString());
        console.log('💾 Saved to localStorage - Total unread:', totalUnread);
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
    
    // Throttle event dispatching
    if (window.bottomNavUpdateTimeout) {
      clearTimeout(window.bottomNavUpdateTimeout);
    }
    
    window.bottomNavUpdateTimeout = setTimeout(() => {
      window.dispatchEvent(new Event('chatUpdated'));
      window.dispatchEvent(new Event('conversationsUpdated'));
      window.dispatchEvent(new CustomEvent('unreadCountUpdated', { 
        detail: { count: conversationsRef.current.reduce((total, conv) => total + (conv.unread_count || 0), 0) }
      }));
    }, 500);
  }, []);

  // Update refs without triggering re-renders
  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  // FIXED: Only trigger updates on meaningful changes
  useEffect(() => {
    if (conversations.length > 0) {
      const totalUnread = conversations.reduce((total, conv) => total + (conv.unread_count || 0), 0);
      const currentUnread = localStorage.getItem('global_unread_count');
      
      if (currentUnread !== totalUnread.toString()) {
        triggerBottomNavUpdate();
      }
    }
  }, [conversations, triggerBottomNavUpdate]);

  const {
    initialize,
    subscribeToUserChannel,
    subscribeToConversation,
    joinPresenceChannel,
    leaveConversation,
    disconnect,
    isConnected: websocketConnected
  } = useWebSocket(jwtToken, currentUserId);

  const markMessagesAsRead = useCallback(async (messageIds) => {
    try {
      if (!messageIds || messageIds.length === 0) return;

      const validMessageIds = messageIds.filter(id => id != null && id !== '');
      if (validMessageIds.length === 0) return;

      try {
        await api.post('/chat/mark-as-read', {
          message_ids: validMessageIds
        });
      } catch (error) {
        // Silent fail for 404
      }

      setMessages(prev => prev.map(msg => 
        validMessageIds.includes(msg.id) ? { ...msg, is_read: true } : msg
      ));
      
      if (activeConversation?.id) {
        setConversations(prev => prev.map(conv => 
          conv.id === activeConversation.id ? { ...conv, unread_count: 0 } : conv
        ));
      }
      
    } catch (error) {
      // Silent error handling
    }
  }, [activeConversation?.id]);

  const sendTypingIndicator = useCallback(async (conversationId, isTyping) => {
    if (!conversationId || conversationId.startsWith('temp-')) return;
    
    try {
      if (typingDebounceRef.current) {
        clearTimeout(typingDebounceRef.current);
      }
      
      typingDebounceRef.current = setTimeout(async () => {
        await api.post('/chat/typing', {
          conversation_id: conversationId,
          is_typing: isTyping
        });
      }, 300);
      
    } catch (error) {
      // Silent error handling
    }
  }, []);

  const handleGlobalMessageNotification = useCallback((event) => {
    if (!event || !event.message) return;

    const message = event.message;
    const currentUserIdStr = currentUserId?.toString();
    const currentActiveConversation = activeConversationRef.current;

    setConversations(prev => {
      let conversationExists = null;
      
      if (message.conversation_id) {
        conversationExists = prev.find(conv => conv.id === message.conversation_id);
      }
      
      if (!conversationExists) {
        const otherUserId = message.sender_id.toString() !== currentUserIdStr 
          ? message.sender_id 
          : message.recipient_id;
        
        conversationExists = prev.find(conv => {
          if (conv.other_user?.id == otherUserId) return true;
          if (conv.participants && Array.isArray(conv.participants)) {
            return conv.participants.some(p => p.id == otherUserId);
          }
          return false;
        });
      }

      if (!conversationExists) {
        const isCurrentUserSender = message.sender_id.toString() === currentUserIdStr;
        const otherUserId = isCurrentUserSender ? message.recipient_id : message.sender_id;
        
        const otherUserData = {
          id: otherUserId,
          name: isCurrentUserSender 
            ? (message.recipient_name || message.recipient_full_name || 'User')
            : (message.sender_name || message.sender_full_name || 'User'),
          email: isCurrentUserSender ? message.recipient_email : message.sender_email,
          avatar: isCurrentUserSender ? message.recipient_avatar : message.sender_avatar,
        };

        const newConversation = {
          id: message.conversation_id,
          other_user: otherUserData,
          last_message: message,
          unread_count: message.sender_id.toString() !== currentUserIdStr ? 1 : 0,
          created_at: message.created_at,
          updated_at: message.created_at
        };
        
        const updatedConversations = [newConversation, ...prev];
        
        return updatedConversations;
      }

      const updated = prev.map(conv => {
        const shouldUpdate = 
          conv.id === message.conversation_id || 
          (conv.other_user?.id == (message.sender_id.toString() !== currentUserIdStr ? message.sender_id : message.recipient_id));
        
        if (shouldUpdate) {
          const updatedId = message.conversation_id || conv.id;
          
          let updatedOtherUser = { ...conv.other_user };
          if (!updatedOtherUser.name || updatedOtherUser.name === 'User') {
            const isCurrentUserSender = message.sender_id.toString() === currentUserIdStr;
            updatedOtherUser = {
              ...updatedOtherUser,
              name: isCurrentUserSender 
                ? (message.recipient_name || message.recipient_full_name || updatedOtherUser.name)
                : (message.sender_name || message.sender_full_name || updatedOtherUser.name),
            };
          }
          
          const unreadIncrement = message.sender_id.toString() !== currentUserIdStr && 
                         (!currentActiveConversation || currentActiveConversation.id !== conv.id) ? 1 : 0;
          
          return {
            ...conv,
            id: updatedId,
            other_user: updatedOtherUser,
            last_message: message,
            unread_count: (conv.unread_count || 0) + unreadIncrement,
            updated_at: message.created_at
          };
        }
        return conv;
      });
      
      const updatedConv = updated.find(conv => 
        conv.id === message.conversation_id || 
        conv.other_user?.id == (message.sender_id.toString() !== currentUserIdStr ? message.sender_id : message.recipient_id)
      );
      
      let finalConversations;
      if (updatedConv) {
        finalConversations = [updatedConv, ...updated.filter(conv => 
          conv.id !== updatedConv.id && 
          (!conv.other_user || conv.other_user.id !== updatedConv.other_user?.id)
        )];
      } else {
        finalConversations = updated;
      }
      
      return finalConversations;
    });

    if (currentActiveConversation && 
        (currentActiveConversation.id === message.conversation_id ||
         currentActiveConversation.other_user?.id == (message.sender_id.toString() !== currentUserIdStr ? message.sender_id : message.recipient_id))) {
      
      setMessages(prev => {
        if (prev.some(msg => 
          msg.id === message.id || 
          (msg.reference_number && msg.reference_number === message.reference_number)
        )) {
          return prev;
        }
        return [...prev, message];
      });

      if (message.sender_id.toString() !== currentUserIdStr) {
        markMessagesAsRead([message.id]);
      }
      
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
    
  }, [currentUserId, markMessagesAsRead]);

  useEffect(() => {
    if (!jwtToken || !currentUserId) return;

    const echo = initialize();
    
    if (echo) {
      setIsConnected(true);
      
      subscribeToUserChannel(
        (event) => {
          handleGlobalMessageNotification(event);
        },
        () => {}
      );
    }

    return () => {
      disconnect();
    };
  }, [jwtToken, currentUserId, initialize, subscribeToUserChannel, disconnect, handleGlobalMessageNotification]);

  useEffect(() => {
    if (!activeConversation || (!activeConversation.id && !activeConversation.other_user?.id)) {
      return;
    }

    const channelIdentifier = activeConversation.id || `temp-${activeConversation.other_user.id}`;

    return () => {
      leaveConversation(channelIdentifier);
    };
  }, [activeConversation, leaveConversation]);

  useEffect(() => {
    if (activeConversation?.id) {
      const conversationId = activeConversation.id;
      const currentUserIdStr = currentUserId?.toString();

      if (!conversationId.startsWith('temp-')) {
        const conversationChannel = subscribeToConversation(
          conversationId,
          (message) => {
            if (message.sender_id.toString() !== currentUserIdStr) {
              setMessages(prev => {
                if (prev.some(msg => msg.id === message.id || (msg.reference_number && msg.reference_number === message.reference_number))) {
                  return prev;
                }
                return [...prev, message];
              });
              
              if (message.id) {
                markMessagesAsRead([message.id]);
              }
              
              scrollToBottom();
            } else {
              setMessages(prev => prev.map(msg => 
                msg.reference_number === message.reference_number ? message : msg
              ));
            }
          },
          (typingEvent) => {
            if (typingEvent.user.id.toString() !== currentUserIdStr) {
              handleTypingIndicator(typingEvent);
            }
          }
        );

        const presenceChannel = joinPresenceChannel(
          conversationId,
          (users) => {
            setOnlineUsers(users || []);
          },
          (user) => {
            setOnlineUsers(prev => {
              const filtered = prev.filter(u => u.id !== user.id);
              return [...filtered, user];
            });
          },
          (user) => {
            setOnlineUsers(prev => prev.filter(u => u.id !== user.id));
          }
        );

        return () => {
          if (conversationChannel || presenceChannel) {
            leaveConversation(conversationId);
          }
        };
      }
    }
  }, [activeConversation?.id, currentUserId, subscribeToConversation, joinPresenceChannel, leaveConversation, markMessagesAsRead]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  };

  const refreshChat = async () => {
    setRefreshing(true);
    try {
      await loadConversations();
      if (activeConversation) {
        await loadMessages(activeConversation.other_user.id);
      }
    } catch (error) {
      // Silent error
    } finally {
      setRefreshing(false);
    }
  };

  const loadConversations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/chat/conversations');
      
      let conversationsData = [];
      
      if (response.data.success && response.data.data && Array.isArray(response.data.data.conversations)) {
        conversationsData = response.data.data.conversations;
      } else if (Array.isArray(response.data)) {
        conversationsData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        conversationsData = response.data.data;
      }
      
      const processedConversations = conversationsData.map(conv => {
        if (!conv.other_user && conv.participants && Array.isArray(conv.participants)) {
          const otherParticipant = conv.participants.find(
            participant => participant.id && participant.id.toString() !== currentUserId?.toString()
          );
          if (otherParticipant) {
            return {
              ...conv,
              other_user: {
                id: otherParticipant.id,
                name: otherParticipant.full_name || 
                      (otherParticipant.first_name && otherParticipant.surname ? 
                       `${otherParticipant.first_name} ${otherParticipant.surname}` : 
                       otherParticipant.name) ||
                      otherParticipant.email ||
                      'User',
                email: otherParticipant.email,
                avatar: otherParticipant.passport_photo || otherParticipant.avatar || otherParticipant.profile_picture,
              }
            };
          }
        }
        return conv;
      });
      
      setConversations(processedConversations);
      
    } catch (error) {
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (search = '') => {
    try {
      setUsersLoading(true);
      const response = await api.get('/admins/members', {
        params: {
          search: search,
          per_page: 50
        }
      });
      
      let usersData = [];
      
      if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
        usersData = response.data.data;
      } else if (Array.isArray(response.data)) {
        usersData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        usersData = response.data.data;
      } else if (response.data && Array.isArray(response.data.users)) {
        usersData = response.data.users;
      } else if (response.data && Array.isArray(response.data.members)) {
        usersData = response.data.members;
      }
      
      const filteredUsers = usersData
        .filter(user => user && user.id && user.id.toString() !== currentUserId?.toString())
        .map(user => {
          let displayName = 'Unknown User';
          
          if (user.full_name && user.full_name.trim() !== '') {
            displayName = user.full_name;
          } else if (user.first_name && user.surname) {
            displayName = `${user.first_name} ${user.surname}`.trim();
          } else if (user.first_name) {
            displayName = user.first_name;
          } else if (user.surname) {
            displayName = user.surname;
          } else if (user.name && user.name.trim() !== '') {
            displayName = user.name;
          } else if (user.username && user.username.trim() !== '') {
            displayName = user.username;
          } else if (user.email) {
            displayName = user.email;
          }
          
          return {
            id: user.id,
            name: displayName,
            email: user.email || 'No email',
            avatar: user.passport_photo || user.avatar || user.profile_picture,
            is_online: user.is_online || false,
          };
        });
      
      setUsers(filteredUsers);
    } catch (error) {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const startNewConversation = async (user) => {
    try {
      setLoading(true);
      
      const response = await api.get(`/chat/conversation/${user.id}`);
      
      let conversationData = null;
      
      if (response.data.success && response.data.data) {
        conversationData = response.data.data;
      } else if (response.data) {
        conversationData = response.data;
      }
      
      if (conversationData) {
        const conversation = {
          id: conversationData.conversation_id || null,
          other_user: {
            id: user.id,
            name: user.name,
            email: user.email,
            avatar: user.avatar,
          },
          last_message: conversationData.messages?.[conversationData.messages.length - 1],
          unread_count: 0,
          messages: conversationData.messages || []
        };
        
        setConversations(prev => {
          const existsById = conversation.id && prev.find(conv => conv.id === conversation.id);
          const existsByUserId = prev.find(conv => conv.other_user?.id === user.id);
          
          let updatedConversations;
          if (existsById) {
            updatedConversations = prev.map(conv => conv.id === conversation.id ? conversation : conv);
          } else if (existsByUserId) {
            updatedConversations = prev.map(conv => conv.other_user?.id === user.id ? { ...conv, ...conversation } : conv);
          } else {
            updatedConversations = [conversation, ...prev];
          }
          
          return updatedConversations;
        });
        
        selectConversation(conversation);
        setShowNewChat(false);
        setUserSearchTerm('');
        setShowSidebar(false);
      } else {
        throw new Error('No conversation data received');
      }
      
    } catch (error) {
      alert('Failed to start conversation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (userId) => {
    try {
      setLoading(true);
      
      const response = await api.get(`/chat/conversation/${userId}`);
      
      let messagesData = [];
      let conversationId = null;
      
      if (response.data.success && response.data.data) {
        messagesData = response.data.data.messages || [];
        conversationId = response.data.data.conversation_id;
      } else if (response.data && Array.isArray(response.data.messages)) {
        messagesData = response.data.messages;
        conversationId = response.data.conversation_id;
      } else if (response.data && Array.isArray(response.data.data)) {
        messagesData = response.data.data;
      } else if (Array.isArray(response.data)) {
        messagesData = response.data;
      }
      
      setMessages(messagesData.reverse());
      
      if (conversationId && activeConversation && activeConversation.other_user?.id) {
        if (activeConversation.id !== conversationId) {
          setActiveConversation(prev => ({
            ...prev,
            id: conversationId
          }));
          
          setConversations(prev => prev.map(conv => 
            conv.other_user?.id === userId ? { ...conv, id: conversationId } : conv
          ));
        }
      }
      
      const unreadMessages = messagesData.filter(msg => 
        !msg.is_read && msg.sender_id.toString() !== currentUserId
      );
      
      if (unreadMessages.length > 0) {
        const unreadMessageIds = unreadMessages.map(msg => msg.id).filter(id => id != null);
        if (unreadMessageIds.length > 0) {
          await markMessagesAsRead(unreadMessageIds);
        }
      }
      
    } catch (error) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTypingIndicator = (typingEvent) => {
    if (typingEvent.is_typing) {
      setTypingUsers(prev => [...prev.filter(u => u.id !== typingEvent.user.id), typingEvent.user]);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        setTypingUsers(prev => prev.filter(u => u.id !== typingEvent.user.id));
      }, 3000);
    } else {
      setTypingUsers(prev => prev.filter(u => u.id !== typingEvent.user.id));
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    const conversationId = activeConversation?.id;
    
    if (conversationId && !conversationId.startsWith('temp-')) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      sendTypingIndicator(conversationId, true);
      
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingIndicator(conversationId, false);
      }, 1000);
    }
  };

  const selectConversation = (conversation) => {
    console.log('🎯 Selecting conversation with user ID:', conversation.other_user?.id);
    setActiveConversation(conversation);
    setShowSidebar(false);
    
    // Mark conversation as read when selected
    if (conversation.unread_count > 0) {
      setConversations(prev => prev.map(conv => 
        conv.id === conversation.id ? { ...conv, unread_count: 0 } : conv
      ));
    }
    
    if (conversation.other_user) {
      loadMessages(conversation.other_user.id);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && attachments.length === 0) return;

    // Define referenceNumber here so it's available in the entire function scope
    const referenceNumber = `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    try {
      if (!activeConversation || !activeConversation.other_user) {
        throw new Error('No active conversation selected');
      }

      const recipientId = activeConversation.other_user.id;
      const messageContent = newMessage.trim();

      const optimisticMessage = {
        id: null,
        conversation_id: activeConversation.id,
        sender_id: currentUserId,
        content: messageContent,
        created_at: new Date().toISOString(),
        is_read: false,
        reference_number: referenceNumber,
      };
      setMessages(prev => [...prev, optimisticMessage]);
      scrollToBottom();

      let response;
      let endpoint = '/chat/send';
      
      if (attachments.length > 0) {
        const formData = new FormData();
        formData.append('recipient_id', recipientId);
        formData.append('message_type', 'file');
        formData.append('content', messageContent);
        formData.append('reference_number', referenceNumber);
        attachments.forEach(file => formData.append('attachments[]', file));

        response = await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        response = await api.post(endpoint, {
          recipient_id: recipientId,
          message_type: 'text',
          content: messageContent,
          reference_number: referenceNumber,
        });
      }

      if (response.data.success && response.data.data && response.data.data.message) {
        const sentMessage = response.data.data.message;
        const resolvedConversationId = response.data.data.conversation_id || activeConversation.id;

        setMessages(prev => prev.map(msg => 
          msg.reference_number === referenceNumber 
            ? { ...sentMessage, reference_number: null }
            : msg
        ));
        
        setConversations(prev => {
          const updated = prev.map(conv => {
            if (conv.other_user?.id === recipientId) {
              return {
                ...conv,
                id: resolvedConversationId,
                last_message: sentMessage
              };
            }
            return conv;
          });
          
          const updatedConv = updated.find(conv => conv.id === resolvedConversationId);
          let finalConversations;
          if (updatedConv) {
            finalConversations = [updatedConv, ...updated.filter(conv => conv.id !== resolvedConversationId)];
          } else {
            finalConversations = updated;
          }
          
          return finalConversations;
        });

        if (activeConversation.id !== resolvedConversationId) {
          setActiveConversation(prev => ({ ...prev, id: resolvedConversationId }));
        }

      } else {
        throw new Error('Invalid response data after sending message.');
      }

      setNewMessage('');
      setAttachments([]);
      adjustTextareaHeight();
      
      const conversationId = activeConversation.id;
      if (conversationId && !conversationId.startsWith('temp-')) {
        sendTypingIndicator(conversationId, false);
      }
      
    } catch (error) {
      // Now referenceNumber is available in the catch block
      setMessages(prev => prev.filter(msg => msg.reference_number !== referenceNumber));
      alert('Failed to send message. Please try again.');
    }
  };

  const findAdminHelper = async () => {
    try {
      const response = await api.get('/chat/find-admin-helper');
      
      let adminHelper = null;
      
      if (response.data.success && response.data.data && response.data.data.admin_helper) {
        adminHelper = response.data.data.admin_helper;
      }
      
      if (adminHelper) {
        let adminName = 'Admin Helper';
        if (adminHelper.full_name && adminHelper.full_name.trim() !== '') {
          adminName = adminHelper.full_name;
        } else if (adminHelper.first_name && adminHelper.surname) {
          adminName = `${adminHelper.first_name} ${adminHelper.surname}`;
        }
        
        const adminUser = {
          id: adminHelper.id,
          name: adminName,
          email: adminHelper.email || 'No email',
          avatar: adminHelper.passport_photo,
          is_online: true
        };
        
        await startNewConversation(adminUser);
      } else {
        alert('No admin helper available at the moment.');
      }
    } catch (error) {
      alert('Failed to connect with admin helper.');
    }
  };

  const handleFileAttach = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const openNewChat = () => {
    setShowNewChat(true);
    setUserSearchTerm('');
    loadUsers();
  };

  const handleUserSearch = (e) => {
    const search = e.target.value;
    setUserSearchTerm(search);
    loadUsers(search);
  };

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const handleBack = () => {
    setActiveConversation(null);
    setShowSidebar(true);
  };

  const getOtherUser = (conversation) => {
    if (!conversation) return { name: 'Unknown User', id: null };
    
    if (conversation.other_user) {
      const otherUser = { ...conversation.other_user };
      
      if (!otherUser.name || otherUser.name === 'Unknown User' || otherUser.name === 'User') {
        if (otherUser.full_name && otherUser.full_name.trim() !== '') {
          otherUser.name = otherUser.full_name;
        } else if (otherUser.first_name && otherUser.surname) {
          otherUser.name = `${otherUser.first_name} ${otherUser.surname}`.trim();
        } else if (otherUser.first_name) {
          otherUser.name = otherUser.first_name;
        } else if (otherUser.surname) {
          otherUser.name = otherUser.surname;
        } else if (otherUser.email) {
          otherUser.name = otherUser.email.split('@')[0];
        } else if (otherUser.username) {
          otherUser.name = otherUser.username;
        } else {
          otherUser.name = 'User';
        }
      }
      
      return otherUser;
    }
    
    if (conversation.participants && Array.isArray(conversation.participants)) {
      const otherParticipant = conversation.participants.find(
        participant => participant.id && participant.id.toString() !== currentUserId?.toString()
      );
      
      if (otherParticipant) {
        let displayName = 'User';
        
        if (otherParticipant.full_name && otherParticipant.full_name.trim() !== '') {
          displayName = otherParticipant.full_name;
        } else if (otherParticipant.first_name && otherParticipant.surname) {
          displayName = `${otherParticipant.first_name} ${otherParticipant.surname}`.trim();
        } else if (otherParticipant.first_name) {
          displayName = otherParticipant.first_name;
        } else if (otherParticipant.surname) {
          displayName = otherParticipant.surname;
        } else if (otherParticipant.name && otherParticipant.name.trim() !== '') {
          displayName = otherParticipant.name;
        } else if (otherParticipant.email) {
          displayName = otherParticipant.email.split('@')[0];
        } else if (otherParticipant.username) {
          displayName = otherParticipant.username;
        }
        
        return {
          id: otherParticipant.id,
          name: displayName,
          avatar: otherParticipant.avatar || otherParticipant.passport_photo || otherParticipant.profile_picture,
          email: otherParticipant.email,
        };
      }
    }
    
    if (conversation.last_message) {
      const lastMessage = conversation.last_message;
      const isCurrentUserSender = lastMessage.sender_id.toString() === currentUserId?.toString();
      
      if (isCurrentUserSender && lastMessage.recipient_name) {
        return {
          id: lastMessage.recipient_id,
          name: lastMessage.recipient_name,
          email: lastMessage.recipient_email,
          avatar: lastMessage.recipient_avatar
        };
      } else if (!isCurrentUserSender && lastMessage.sender_name) {
        return {
          id: lastMessage.sender_id,
          name: lastMessage.sender_name,
          email: lastMessage.sender_email,
          avatar: lastMessage.sender_avatar
        };
      }
    }
    
    return { name: 'User', id: null };
  };

  const getLastMessage = (conversation) => {
    if (!conversation) return { content: 'No messages yet' };
    return conversation.last_message || { content: 'No messages yet' };
  };

  const isUserOnline = (userId) => {
    if (!userId) {
      return false;
    }
    
    const isOnline = onlineUsers.some(user => user && user.id && user.id.toString() === userId.toString());
    return isOnline;
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    } else {
      return date.toLocaleDateString([], {month: 'short', day: 'numeric'});
    }
  };

  const filteredConversations = Array.isArray(conversations) ? conversations.filter(conv => {
    if (!conv) return false;
    
    const otherUser = getOtherUser(conv);
    const lastMessage = getLastMessage(conv);
    
    const otherUserName = otherUser?.name || '';
    const lastMessageContent = lastMessage?.content || '';
    
    return (
      otherUserName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lastMessageContent.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }) : [];

  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    if (!user) return false;
    
    const userName = user.name || '';
    const userEmail = user.email || '';
    
    return (
      userName.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      userEmail.toLowerCase().includes(userSearchTerm.toLowerCase())
    );
  }) : [];

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [newMessage]);

  useEffect(() => {
    setIsConnected(websocketConnected);
  }, [websocketConnected]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (typingDebounceRef.current) {
        clearTimeout(typingDebounceRef.current);
      }
      if (window.bottomNavUpdateTimeout) {
        clearTimeout(window.bottomNavUpdateTimeout);
      }
    };
  }, []);

  return (
    <div className="chat-container">
      <div className={`chat-sidebar ${showSidebar || !activeConversation ? 'active' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-header-top">
            <h2>Messages</h2>
            <div className="header-actions">
              <button className="icon-button" onClick={openNewChat} title="New Chat">
                <FiPlus />
              </button>
              <button className="icon-button" onClick={findAdminHelper} title="Contact Admin">
                <FiUsers />
              </button>
              <button 
                className={`icon-button ${refreshing ? 'refreshing' : ''}`} 
                onClick={refreshChat} 
                title="Refresh"
                disabled={refreshing}
              >
                <FiRefreshCw className={refreshing ? 'spinning' : ''} />
              </button>
            </div>
          </div>
          <div className="connection-status">
            <div className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}></div>
            {refreshing && <span className="refreshing-text">Refreshing...</span>}
          </div>
        </div>

        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="conversations-list">
          {loading ? (
            <div className="loading">Loading conversations...</div>
          ) : filteredConversations.length === 0 ? (
            <div className="no-conversations">
              <FiMessageCircle size={48} />
              <p>No conversations yet</p>
              <button className="start-chat-btn" onClick={openNewChat}>
                Start a chat
              </button>
            </div>
          ) : (
            filteredConversations.map(conversation => {
              const otherUser = getOtherUser(conversation);
              const lastMessage = getLastMessage(conversation);
              const isOnline = isUserOnline(otherUser.id);
              const lastMessageTime = lastMessage?.created_at ? formatMessageTime(lastMessage.created_at) : '';
              
              return (
                <div
                  key={conversation.id || `temp-conv-${otherUser.id}`}
                  className={`conversation-item ${activeConversation?.id === conversation.id || (!activeConversation?.id && activeConversation?.other_user?.id === otherUser.id) ? 'active' : ''}`}
                  onClick={() => selectConversation(conversation)}
                >
                  <div className="avatar">
                    {otherUser.avatar ? (
                      <img src={otherUser.avatar} alt={otherUser.name} />
                    ) : (
                      <div className="avatar-placeholder">
                        {otherUser.name?.charAt(0) || 'U'}
                      </div>
                    )}
                    <div className={`online-indicator ${isOnline ? 'online' : 'offline'}`}></div>
                  </div>
                  <div className="conversation-info">
                    <div className="conversation-header">
                      <h4>{otherUser.name || 'Unknown User'}</h4>
                      <div className="conversation-meta">
                        {lastMessageTime && (
                          <span className="conversation-time">{lastMessageTime}</span>
                        )}
                        {conversation.unread_count > 0 && (
                          <div className="unread-badge">
                            {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="last-message">
                      {lastMessage.content || 'No messages yet'}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showNewChat && (
        <div className="modal-overlay" onClick={() => setShowNewChat(false)}>
          <div className="new-chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>New Chat</h3>
              <button className="icon-button" onClick={() => setShowNewChat(false)}>
                <FiX />
              </button>
            </div>
            
            <div className="modal-search">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={userSearchTerm}
                onChange={handleUserSearch}
                autoFocus
              />
            </div>
            
            <div className="users-list">
              {usersLoading ? (
                <div className="loading">Loading users...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="no-users">
                  <FiUser size={48} />
                  <p>{userSearchTerm ? 'No users found' : 'No users available'}</p>
                  {userSearchTerm && (
                    <p className="search-tip">Try a different search term</p>
                  )}
                </div>
              ) : (
                <div className="users-list-content">
                  <div className="users-count">
                    {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
                  </div>
                  {filteredUsers.map(user => (
                    <div
                      key={user.id}
                      className="user-item"
                      onClick={() => startNewConversation(user)}
                    >
                      <div className="avatar">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} />
                        ) : (
                          <div className="avatar-placeholder">
                            {user.name?.charAt(0) || 'U'}
                          </div>
                        )}
                        <div className={`online-indicator ${user.is_online ? 'online' : 'offline'}`}></div>
                      </div>
                      <div className="user-info">
                        <h4>{user.name}</h4>
                        <p className="user-email">{user.email}</p>
                      </div>
                      <div className="start-chat-indicator">
                        <FiMessageCircle />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="chat-main">
        {activeConversation ? (
          <>
            <div className="chat-header">
              <button className="icon-button back-button" onClick={handleBack}>
                <FiArrowLeft />
              </button>
              <div className="chat-user-info">
                <div className="avatar">
                  {getOtherUser(activeConversation).avatar ? (
                    <img src={getOtherUser(activeConversation).avatar} alt={getOtherUser(activeConversation).name} />
                  ) : (
                    <div className="avatar-placeholder">
                      {getOtherUser(activeConversation).name?.charAt(0) || 'U'}
                    </div>
                  )}
                  <div className={`online-indicator ${isUserOnline(getOtherUser(activeConversation).id) ? 'online' : 'offline'}`}></div>
                </div>
                <div>
                  <h3>{getOtherUser(activeConversation).name || 'Unknown User'}</h3>
                  <span className="user-status">
                    {isUserOnline(getOtherUser(activeConversation).id) ? '🟢 Online' : '🔴 Offline'}
                  </span>
                </div>
              </div>
            </div>

            <div className="messages-container">
              {loading ? (
                <div className="loading">Loading messages...</div>
              ) : messages.length === 0 ? (
                <div className="no-messages">
                  <FiMessageSquare size={64} />
                  <p>No messages yet</p>
                  <span>Start a conversation by sending a message</span>
                </div>
              ) : (
                <div className="messages-list">
                  {messages.map((message) => (
                    <div
                      key={message.id || `msg-${message.reference_number || Date.now()}`}
                      className={`message ${message.sender_id == currentUserId ? 'sent' : 'received'}`}
                    >
                      <div className="message-content">
                        <p>{message.content}</p>
                        {message.attachments && message.attachments.length > 0 && (
                          <div className="message-attachments">
                            {message.attachments.map((attachment, idx) => (
                              <div key={idx} className="attachment">
                                {attachment.type?.startsWith('image/') ? (
                                  <img src={attachment.url} alt="Attachment" />
                                ) : (
                                  <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                                    <FiFile /> {attachment.name}
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <span className="message-time">
                          {new Date(message.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          {message.sender_id == currentUserId && (
                            <span className="read-status">
                              {message.is_read ? ' ✓✓' : ' ✓'}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                  {typingUsers.map(user => (
                    <div key={user.id} className="typing-indicator">
                      {user.name} is typing...
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {attachments.length > 0 && (
              <div className="attachments-preview">
                {attachments.map((file, index) => (
                  <div key={index} className="attachment-preview">
                    {file.type.startsWith('image/') ? (
                      <img src={URL.createObjectURL(file)} alt="Preview" />
                    ) : (
                      <div className="file-preview">
                        <FiFile />
                        <span>{file.name}</span>
                      </div>
                    )}
                    <button onClick={() => removeAttachment(index)} className="remove-attachment">
                      <FiX />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={sendMessage} className="message-input-form">
              <div className="message-input-container">
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FiPaperclip />
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  multiple
                  onChange={handleFileAttach}
                />
                <textarea
                  ref={textareaRef}
                  className="message-input"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage(e);
                    }
                  }}
                  rows={1}
                />
                <button 
                  type="submit" 
                  className="icon-button send-button" 
                  disabled={!newMessage.trim() && attachments.length === 0}
                >
                  <FiSend />
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="no-chat-selected">
            <FiMessageCircle size={80} />
            <h3>Select a conversation</h3>
            <p>Choose a conversation from the sidebar to start messaging</p>
            <button className="start-chat-btn large" onClick={openNewChat}>
              Start New Chat
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat;